import { Col, Row } from "react-grid-system";
import { useChannelState } from "../../lib/channelContext";
import {
  NoPaymentData,
  ResultDataByTab,
  ResultQuickClickOption,
} from "./resultTypes";
import StyledInput from "../common/inputs/StyledInput";
import {
  getQuickClickOptions,
  getResultActionEntry,
  getNoPaymentWorkDateMinMax,
} from "./resultUtils";
import { useEffect } from "react";
import styles from "./Results.module.css";
import PermCollQueue from "./PermCollQueue";
import {
  ResultDropdowns,
  ResultOption,
  ValidationErrors,
} from "../../lib/api/models";
import Select from "../common/inputs/Select";
import classNames from "classnames";
import DateInput from "../common/inputs/DateInput";
import AutocompleteInput from "../common/inputs/AutocompleteInput";

const strings = {
  select: "Select",
  enter: "Enter",

  actionCd: "Action Cd",
  who: "Who",
  correspondenceReason: "Corr. Reason",

  other: "Other",
  transferSource: "Transfer Source",
  callReason: "Call Reason",
  quickClick: "Quick Click",

  actionEntry: "Action Entry",
  actionNote: "Action Note",
  workDate: "Work Date",
};

type Props = {
  dwbuid: string;
  data: NoPaymentData;
  setData: React.Dispatch<React.SetStateAction<ResultDataByTab>>;
  options: ResultDropdowns;
  quickClick: ResultQuickClickOption | null;
  setQuickClick: React.Dispatch<
    React.SetStateAction<ResultQuickClickOption | null>
  >;
  quickClickValue: string;
  setQuickClickValue: React.Dispatch<React.SetStateAction<string>>;
  setContactPerson: (cp: ResultOption) => void;
  onDisplayGeneralErrors: (error: unknown) => void;
  validationMessages?: ValidationErrors;
  setValidationMessages: React.Dispatch<
    React.SetStateAction<ValidationErrors | undefined>
  >;
  referenceId?: string;
};

const MAX_ACTION_ENTRY_LEN = 19;

export default function NoPaymentContent({
  dwbuid,
  data,
  setData,
  options,
  quickClick,
  setQuickClick,
  quickClickValue,
  setQuickClickValue,
  setContactPerson,
  onDisplayGeneralErrors,
  validationMessages,
  setValidationMessages,
  referenceId,
}: Props) {
  const { channel } = useChannelState();

  // Action Note can only be as long as 19 - (the length of the other action entry values)
  // If any of the other values change to expand the length past 19, truncate and update the action note
  useEffect(() => {
    const actionEntry = getResultActionEntry({
      tab: "NOPAYMENT",
      data: { ...data, actionNote: "" },
    });

    if (actionEntry.length + data.actionNote.length > MAX_ACTION_ENTRY_LEN) {
      setData((d) => ({
        ...d,
        NOPAYMENT: {
          ...d["NOPAYMENT"],
          actionNote: data.actionNote.slice(
            0,
            MAX_ACTION_ENTRY_LEN - actionEntry.length,
          ),
        },
      }));
    }
  }, [data, setData]);

  const setDataHandleQuickClickAndValidation = (
    newData: Partial<NoPaymentData>,
  ) => {
    // Clear validation for editted fields
    setValidationMessages((v) => {
      let remainingValidation: ValidationErrors = v ?? {};
      Object.keys(newData).forEach((key) => {
        const { [`NOPAYMENT.${key}`]: _, ...rest } = remainingValidation;
        remainingValidation = rest;
      });
      return remainingValidation;
    });
    // Clear Quick Click if applicable
    if (
      quickClick &&
      ((newData.actionCode &&
        quickClick.selections.actionCode !== newData.actionCode.id) ||
        (newData.contactDetail &&
          quickClick.selections.contactDetail !== newData.contactDetail.id) ||
        (newData.other && quickClick.selections.other !== newData.other.id))
    ) {
      setQuickClick(null);
      setQuickClickValue("");
    }
    setData((d) => ({
      ...d,
      NOPAYMENT: {
        ...d["NOPAYMENT"],
        ...newData,
      },
    }));
  };

  return (
    <Row>
      <Col md={3}>
        {options.actionCode && (
          <Select
            variant="horizontal"
            className={classNames(styles.selectContainer, styles.inputDivider)}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.actionCd}
            placeholder={strings.select}
            selectedValue={data.actionCode?.value}
            options={options.actionCode.map((item) => ({
              key: item.id,
              label: item.description,
              value: item.value,
            }))}
            onChange={(value) => {
              if (!options.actionCode) {
                // NOTE: This should not be possible
                return;
              }
              const item = options.actionCode.find((i) => i.value === value);
              if (!item) {
                // NOTE: This should not be possible
                return;
              }
              setDataHandleQuickClickAndValidation({ actionCode: item });
            }}
            validationMessages={validationMessages?.actionCode}
            showPopover={false}
          />
        )}
        {options.who && (
          <Select
            variant="horizontal"
            className={classNames(styles.selectContainer, styles.inputDivider)}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.who}
            placeholder={strings.select}
            selectedValue={data.contactDetail?.value}
            options={options.who.map((item) => ({
              key: item.id,
              label: item.description,
              value: item.value,
            }))}
            onChange={(value) => {
              if (!options.who) {
                // NOTE: This should not be possible
                return;
              }
              const item = options.who.find((i) => i.value === value);
              if (!item) {
                // NOTE: This should not be possible
                return;
              }
              setDataHandleQuickClickAndValidation({ contactDetail: item });
            }}
            validationMessages={validationMessages?.contactDetail}
            showPopover={false}
          />
        )}
        {(channel === "RES" || channel === "COR") &&
          options.correspondenceReason && (
            <Select
              variant="horizontal"
              className={classNames(
                styles.selectContainer,
                styles.inputDivider,
              )}
              innerContentClassName={styles.inputContent}
              labelClassName={styles.inputLabel}
              inputClassName={styles.inputValue}
              label={strings.correspondenceReason}
              placeholder={strings.select}
              selectedValue={data.correspondenceReason?.value}
              options={options.correspondenceReason.map((item) => ({
                key: item.id,
                label: item.description,
                value: item.value,
              }))}
              onChange={(value) => {
                if (!options.correspondenceReason) {
                  // NOTE: This should not be possible
                  return;
                }
                const item = options.correspondenceReason.find(
                  (i) => i.value === value,
                );
                if (!item) {
                  // NOTE: This should not be possible
                  return;
                }
                setDataHandleQuickClickAndValidation({
                  correspondenceReason: item,
                });
              }}
              validationMessages={validationMessages?.correspondenceReason}
              showPopover={false}
            />
          )}
      </Col>
      <Col md={3}>
        {channel === "TRN" && options.transferSource ? (
          <Select
            variant="horizontal"
            className={classNames(styles.selectContainer, styles.inputDivider)}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.transferSource}
            placeholder={strings.select}
            selectedValue={data.transferSource?.value}
            options={options.transferSource.map((item) => ({
              key: item.id,
              label: item.description,
              value: item.value,
            }))}
            onChange={(value) => {
              if (!options.transferSource) {
                // NOTE: This should not be possible
                return;
              }
              const item = options.transferSource.find(
                (i) => i.value === value,
              );
              if (!item) {
                // NOTE: This should not be possible
                return;
              }
              setDataHandleQuickClickAndValidation({ transferSource: item });
            }}
            validationMessages={validationMessages?.transferSource}
            showPopover={false}
          />
        ) : channel === "INB" && options.inboundCallReason ? (
          <Select
            variant="horizontal"
            className={classNames(styles.selectContainer, styles.inputDivider)}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.callReason}
            placeholder={strings.select}
            selectedValue={data.inboundReason?.value}
            options={options.inboundCallReason.map((item) => ({
              key: item.id,
              label: item.description,
              value: item.value,
            }))}
            onChange={(value) => {
              if (!options.inboundCallReason) {
                // NOTE: This should not be possible
                return;
              }
              const item = options.inboundCallReason.find(
                (i) => i.value === value,
              );
              if (!item) {
                // NOTE: This should not be possible
                return;
              }
              setDataHandleQuickClickAndValidation({ inboundReason: item });
            }}
            validationMessages={validationMessages?.inboundReason}
            showPopover={false}
          />
        ) : null}
        {options.other && (
          <Select
            variant="horizontal"
            className={classNames(styles.selectContainer, styles.inputDivider)}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.other}
            placeholder={strings.select}
            selectedValue={data.other?.value}
            options={options.other.map((item) => ({
              key: item.id,
              label: item.description,
              value: item.value,
            }))}
            onChange={(value) => {
              if (!options.other) {
                // NOTE: This should not be possible
                return;
              }
              const item = options.other.find((i) => i.value === value);
              if (!item) {
                // NOTE: This should not be possible
                return;
              }
              setDataHandleQuickClickAndValidation({ other: item });
            }}
            validationMessages={validationMessages?.other}
            showPopover={false}
          />
        )}
        {channel === "RES" || channel === "COR" ? (
          <StyledInput
            disabled={true}
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.actionEntry}
            value={getResultActionEntry({
              tab: "NOPAYMENT",
              data: data,
            })}
          />
        ) : channel === "OUT" ? (
          <AutocompleteInput
            variant="horizontal"
            containerClassName={styles.inputDivider}
            listClassName={styles.quickClickOptions}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.quickClick}
            value={quickClickValue}
            selected={quickClick}
            placeholder={strings.enter}
            onSelect={(o) => {
              const qc = o as ResultQuickClickOption;
              setQuickClick(qc);
              setQuickClickValue(qc.description);

              const actionCode =
                options.actionCode?.find(
                  (i) => i.id === qc.selections.actionCode,
                ) ?? null;
              const contactDetail =
                options.who?.find(
                  (i) => i.id === qc.selections.contactDetail,
                ) ?? null;
              const other =
                options.other?.find((i) => i.id === qc.selections.other) ??
                null;
              const contactPerson =
                options.contactPerson?.find(
                  (i) => i.id === qc.selections.contactPerson,
                ) ?? null;

              if (contactPerson) {
                setContactPerson(contactPerson);
              }

              setDataHandleQuickClickAndValidation({
                actionCode,
                contactDetail,
                other,
              });
            }}
            onChange={(e) => setQuickClickValue(e.target.value)}
            getOptions={getQuickClickOptions}
          />
        ) : null}
      </Col>
      <Col md={3}>
        {channel === "OUT" && (
          <StyledInput
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.actionNote}
            value={data.actionNote}
            onChange={(e) => {
              const actionEntry = getResultActionEntry({
                tab: "NOPAYMENT",
                data: { ...data, actionNote: "" },
              });

              if (
                actionEntry.length + e.target.value.length >
                MAX_ACTION_ENTRY_LEN
              ) {
                return;
              }

              setDataHandleQuickClickAndValidation({
                actionNote: e.target.value,
              });
            }}
            onBlur={() =>
              setData((d) => ({
                ...d,
                NOPAYMENT: {
                  ...d["NOPAYMENT"],
                  actionNote: d["NOPAYMENT"].actionNote.replaceAll(" ", ""),
                },
              }))
            }
            validationMessages={validationMessages?.actionNote}
            showPopover={false}
          />
        )}
        {(channel === "INB" || channel === "TRN" || channel === "OUT") && (
          <StyledInput
            disabled={true}
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.actionEntry}
            value={getResultActionEntry({
              tab: "NOPAYMENT",
              data: data,
            })}
          />
        )}
        {(channel === "INB" ||
          channel === "TRN" ||
          channel === "RES" ||
          channel === "COR") && (
          <StyledInput
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.actionNote}
            value={data.actionNote}
            onChange={(e) => {
              const actionEntry = getResultActionEntry({
                tab: "NOPAYMENT",
                data: { ...data, actionNote: "" },
              });

              if (
                actionEntry.length + e.target.value.length >
                MAX_ACTION_ENTRY_LEN
              ) {
                return;
              }

              setDataHandleQuickClickAndValidation({
                actionNote: e.target.value,
              });
            }}
            onBlur={() =>
              setData((d) => ({
                ...d,
                NOPAYMENT: {
                  ...d["NOPAYMENT"],
                  actionNote: d["NOPAYMENT"].actionNote.replaceAll(" ", ""),
                },
              }))
            }
            validationMessages={validationMessages?.actionNote}
            showPopover={false}
          />
        )}
        {(channel === "RES" || channel === "COR") && (
          <DateInput
            {...getNoPaymentWorkDateMinMax()}
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.workDate}
            value={data.workDate}
            onChangeDate={(value) =>
              setDataHandleQuickClickAndValidation({ workDate: value })
            }
            validationMessages={validationMessages?.workDate}
            showPopover={false}
          />
        )}
      </Col>
      <Col md={3}>
        {(channel === "INB" || channel === "TRN" || channel === "OUT") && (
          <DateInput
            {...getNoPaymentWorkDateMinMax()}
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.workDate}
            value={data.workDate}
            onChangeDate={(value) =>
              setDataHandleQuickClickAndValidation({ workDate: value })
            }
            validationMessages={validationMessages?.workDate}
            showPopover={false}
          />
        )}
        <PermCollQueue
          dwbuid={dwbuid}
          onDisplayGeneralErrors={onDisplayGeneralErrors}
          channel={channel}
          referenceId={referenceId}
        />
      </Col>
    </Row>
  );
}
